import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  OperationVariables,
  ApolloQueryResult,
  DefaultContext,
  ApolloCache,
  TypedDocumentNode,
  DocumentNode,
  MutationHookOptions,
  MutationTuple,
  useMutation,
  QueryResult,
  QueryHookOptions,
  useQuery,
  useLazyQuery,
  LazyQueryHookOptions,
  LazyQueryResultTuple,
  useSuspenseQuery,
  UseSuspenseQueryResult,
  Unmasked,
  from,
} from '@apollo/client';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { setContext } from '@apollo/client/link/context';
import { writeDB } from './store';
import { I18n } from '../locales/translate';
import { useErrorHandler } from '../util/util';
import introspection from '../generated/possible-types';
import { useEffect, useRef } from 'react';
import { relayStylePagination } from '@apollo/client/utilities';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { sha256 } from 'crypto-hash';

export type ExtractVariables<Document> = Document extends TypedDocumentNode<unknown, infer T> ? T : never;

const authLink = setContext((_, { headers }) => {
  const membershipId = writeDB.get('selectedMembership');
  const modifiedHeaders = {
    ...headers,
    'X-KS-Role': `${membershipId}`,
    'Accept-Language': I18n.locale,
  };
  return {
    uri: `/api/graphql?membership=${membershipId}`,
    headers: modifiedHeaders,
  };
}) as unknown as ApolloLink;

const uploadLink = createUploadLink() as unknown as ApolloLink;

const persistedQueriesLink = createPersistedQueryLink({ sha256, useGETForHashedQueries: true });

// use this to automatically merge the permissions field when requesting the same object in different places
const permissions = {
  merge: (existing: Record<string, boolean>, incoming: Record<string, boolean>): Record<string, boolean> => ({
    ...existing,
    ...incoming,
  }),
};

export const apollo = new ApolloClient({
  headers: {},
  cache: new InMemoryCache({
    // see https://the-guild.dev/blog/graphql-codegen-and-apollo-client-3
    possibleTypes: introspection.possibleTypes,
    typePolicies: {
      VenueLayout: {
        fields: {
          pricingCategories: {
            merge(_existing, incoming) {
              return incoming; // Not paginated. The entire array is returned and replaces the existing one.
            },
          },
          permissions,
        },
      },
      Query: {
        fields: {
          exports: {
            keyArgs: ['type', 'first'],
          },
          turnovers: relayStylePagination(),
          voucherCampaigns: relayStylePagination(),
          products: relayStylePagination(['shopId', 'type', 'terms', 'first', 'categoryIds']),
          categories: relayStylePagination(['shopId', 'type', 'cursor', 'first']),
        },
      },
      VoucherCampaign: {
        fields: {
          voucherCodes: relayStylePagination(['terms', 'cursor', 'first']),
        },
      },
      Event: {
        fields: {
          printingOrders: relayStylePagination(['terms', 'cursor', 'first']),
          automations: relayStylePagination(),
        },
      },
      Statistics: {
        merge: true, // merge the statistics object to prevent overwriting it
        fields: {
          products: relayStylePagination(['$from', '$to', 'period', 'statistic']),
          countries: relayStylePagination(['$from', '$to', 'period', 'statistic']),
          pointsOfSale: relayStylePagination(['$from', '$to', 'period', 'statistic']),
        },
      },
      UtmTrackingStats: {
        merge: true,
      },
    },
  }),
  link: from([persistedQueriesLink, authLink, uploadLink]),
  connectToDevTools: true,
  defaultOptions: {
    watchQuery: {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    },
  },
});

interface ForwardPaginationPageInfo {
  hasNextPage: boolean;
  endCursor?: string | null;
}

export async function queryAllPages<Element, QueryType, Variables>(options: {
  query: TypedDocumentNode<QueryType, Variables>;
  variables: Omit<Variables, 'after'>;
  pageInfoExtractor: (result: ApolloQueryResult<QueryType>) => ForwardPaginationPageInfo | undefined;
  elementExtractor: (result: ApolloQueryResult<QueryType>) => Element[];
}): Promise<Array<Element>> {
  const accumlator: Element[] = [];
  let pageInfo: ForwardPaginationPageInfo | undefined;
  while (pageInfo === undefined || pageInfo.hasNextPage) {
    const variables = { ...options.variables, after: pageInfo?.endCursor };
    const pageResult = await apollo.query({ query: options.query, variables, fetchPolicy: 'network-only' });
    accumlator.push(...options.elementExtractor(pageResult));
    pageInfo = options.pageInfoExtractor(pageResult);
  }

  return accumlator;
}

export function useSuspenseQueryAllPages<QueryType, Variables>(
  query: TypedDocumentNode<QueryType, Variables>,
  options: {
    variables: Omit<Variables, 'after'>;
    pageInfoExtractor: (data: QueryType) => ForwardPaginationPageInfo | undefined;
    mergeResults: (previousData: Unmasked<QueryType>, fetchMoreResult: Unmasked<QueryType>) => Unmasked<QueryType>;
  }
): UseSuspenseQueryResult<QueryType> {
  const queryResult = useSuspenseQuery(query, {
    variables: options.variables,
    fetchPolicy: 'network-only',
  });

  const pageInfo = options.pageInfoExtractor(queryResult.data);

  useEffect(() => {
    if (!pageInfo?.hasNextPage) return;

    queryResult.fetchMore({
      variables: { after: pageInfo.endCursor },
      updateQuery(previousData, { fetchMoreResult }) {
        return options.mergeResults(previousData, fetchMoreResult);
      },
    });
  }, [pageInfo]);

  return queryResult;
}

interface QueryHandlerExtraOptions<TData = unknown> {
  pollInterval?: number;
  pollWhile?: (data?: TData) => boolean;
  reloadKey?: number;
  preserveDataWhileLoading?: boolean;
}

function useExtraQueryHandling<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  { pollWhile, pollInterval, reloadKey, preserveDataWhileLoading }: QueryHandlerExtraOptions<TData>,
  result: QueryResult<TData, TVariables>
): QueryResult<TData, TVariables> {
  const { data, error, loading, startPolling, stopPolling, refetch } = result;
  useErrorHandler(error);

  useEffect(() => {
    if (pollWhile || pollInterval) {
      if (!loading) {
        if (pollWhile === undefined || pollWhile(data)) {
          startPolling(pollInterval || 2000);
        } else {
          stopPolling();
        }
      }
    }
    return () => stopPolling();
  }, [pollWhile, pollInterval, loading, data]);

  useEffect(() => {
    if (reloadKey) {
      refetch();
    }
  }, [reloadKey]);

  const cachedData = useRef<TData | null>(null);
  if (data && preserveDataWhileLoading) {
    cachedData.current = data;
  }

  return { ...result, data: cachedData?.current ?? data };
}

export function useQueryHandler<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: QueryHookOptions<TData, TVariables> & QueryHandlerExtraOptions<TData> = {}
): QueryResult<TData, TVariables> {
  const queryResult = useQuery(query, options);

  return useExtraQueryHandling(options, queryResult);
}

export function useLazyQueryHandler<TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: LazyQueryHookOptions<TData, TVariables> & QueryHandlerExtraOptions<TData> = {}
): LazyQueryResultTuple<TData, TVariables> {
  const queryResult = useLazyQuery(query, options);
  useExtraQueryHandling(options, queryResult[1]);
  return queryResult;
}

export function useMutationHandler<
  TData = unknown,
  TVariables = OperationVariables,
  TContext = DefaultContext,
  TCache extends ApolloCache<unknown> = ApolloCache<unknown>,
>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: MutationHookOptions<TData, TVariables, TContext, TCache>
): MutationTuple<TData, TVariables, TContext, TCache> {
  const [mutationFunction, mutationInfo] = useMutation(mutation, options);
  useErrorHandler(mutationInfo.error);
  return [mutationFunction, mutationInfo];
}

export enum QueryNames {
  VOUCHER_CAMPAIGN = 'VoucherCampaign',
  VOUCHER_CODES = 'VoucherCodes',
  VOUCHER_CAMPAIGN_PRODUCT_EVENTS = 'GetProductsAndEventsByVoucherCampaignId',
  SHOP_EVENTS_AND_MERCH_PRODUCTS = 'GetShopEventsAndMerchProducts',
  MERCH_PRODUCT_CATEGORIES = 'GetMerchProductCategoriesForVoucherCampaign',
}
